import { useEffect, useState } from "react";
import axios from "axios";
import { getAuthToken } from "../../../services/authService-sighup";

export interface RevenueBreakdown {
  PEAK?: number;
  OFF_PEAK?: number;
  TOTAL?: number;
}

export interface RevenueNode {
  label: string;

  yesterday?: RevenueBreakdown;

  month_to_date?: RevenueBreakdown;

  year_to_date?: RevenueBreakdown;

  children?: RevenueNode[];
}

interface RevenueData {
  estimatedTree: RevenueNode[];
}

const emptyBreakdown: RevenueBreakdown = {
  PEAK: 0,
  OFF_PEAK: 0,
  TOTAL: 0,
};

const getBreakdownObject = (
  breakdown: any[] = []
): RevenueBreakdown => ({
  PEAK:
    Number(
      breakdown.find(
        (b) => b.type === "PEAK"
      )?.value || 0
    ),

  OFF_PEAK:
    Number(
      breakdown.find(
        (b) => b.type === "OFF_PEAK"
      )?.value || 0
    ),

  TOTAL:
    Number(
      breakdown.find(
        (b) => b.type === "TOTAL"
      )?.value || 0
    ),
});

const buildLookupMap = (
  nodes: any[] = [],
  map = new Map<string, any>()
) => {

  nodes.forEach((node) => {

    const key =
      node.charge_code ||
      node.metric;

    if (key) {
      map.set(key, node);
    }

    if (node.children?.length) {
      buildLookupMap(
        node.children,
        map
      );
    }
  });

  return map;
};

const buildSortMap = (
  sortOrder: any[] = []
) => {

  const map = new Map<string, number>();

  sortOrder.forEach((item) => {

    // Top level
    if (
      item.MARKET_TYPE &&
      !map.has(item.MARKET_TYPE)
    ) {
      map.set(
        item.MARKET_TYPE,
        item.SORT_ORDER
      );
    }

    // Product level
    if (
      item.PRODUCT &&
      !map.has(item.PRODUCT)
    ) {
      map.set(
        item.PRODUCT,
        item.SORT_ORDER
      );
    }

    // Charge code level
    if (
      item.settlement_charge_code
    ) {
      map.set(
        String(
          item.settlement_charge_code
        ),
        item.SORT_ORDER
      );
    }
  });

  return map;
};
const sortNodes = (
  nodes: any[],
  sortMap: Map<any, any>
): any[] => {

  return [...nodes]
    .sort((a, b) => {

      const aOrder =
        sortMap.get(
          String(
            a.charge_code ||
            a.metric
          )
        ) ?? 9999;

      const bOrder =
        sortMap.get(
          String(
            b.charge_code ||
            b.metric
          )
        ) ?? 9999;

      return aOrder - bOrder;
    })
    .map((node) => ({
      ...node,

      children:
        node.children?.length
          ? sortNodes(
              node.children,
              sortMap
            )
          : [],
    }));
};

const mergePeriods = (
  apiData: any
): RevenueNode[] => {

 const mergeNodes = (
  dayNodes: any[] = [],
  monthNodes: any[] = [],
  yearNodes: any[] = []
): RevenueNode[] => {

  const monthMap =
    buildLookupMap(monthNodes);

  const yearMap =
    buildLookupMap(yearNodes);

  return dayNodes
    .filter(
      (node: any) =>
        node.metric !== "net_revenue"
    )
    .map((dayNode: any) => {

      const key =
        dayNode.charge_code ||
        dayNode.metric;

      // const monthNode =
      //   monthMap.get(key);

      // const yearNode =
      //   yearMap.get(key);
      const monthNode =
  monthNodes.find(
    (m) =>
      (m.charge_code ||
        m.metric) ===
      (dayNode.charge_code ||
        dayNode.metric)
  );

const yearNode =
  yearNodes.find(
    (y) =>
      (y.charge_code ||
        y.metric) ===
      (dayNode.charge_code ||
        dayNode.metric)
  );

      return {
        label:
          dayNode.charge_code
            ? `${dayNode.charge_code} ${dayNode.charge_name}`
            : dayNode.charge_name ||
              dayNode.metric,

        yesterday:
          getBreakdownObject(
            dayNode.breakdown
          ),

        month_to_date:
          monthNode
            ? getBreakdownObject(
                monthNode.breakdown
              )
            : emptyBreakdown,

        year_to_date:
          yearNode
            ? getBreakdownObject(
                yearNode.breakdown
              )
            : emptyBreakdown,

        children:
          mergeNodes(
            dayNode.children || [],
            monthNode?.children || [],
            yearNode?.children || []
          ),
      };
    });
    
};

  return mergeNodes(
    apiData?.day || [],
    apiData?.month_to_date || [],
    apiData?.year_to_date || []
  );
};
const calculateParentTotals = (
  nodes: RevenueNode[]
): RevenueNode[] => {

  return nodes.map((node) => {

    if (
      node.children &&
      node.children.length > 0
    ) {

      node.children =
        calculateParentTotals(
          node.children
        );

      const totals = {
        yesterday: {
          TOTAL: 0,
          PEAK: 0,
          OFF_PEAK: 0,
        },

        month_to_date: {
          TOTAL: 0,
          PEAK: 0,
          OFF_PEAK: 0,
        },

        year_to_date: {
          TOTAL: 0,
          PEAK: 0,
          OFF_PEAK: 0,
        },
      };

      node.children.forEach(
        (child) => {

          totals.yesterday.TOTAL +=
            child.yesterday
              ?.TOTAL || 0;

          totals.yesterday.PEAK +=
            child.yesterday
              ?.PEAK || 0;

          totals.yesterday.OFF_PEAK +=
            child.yesterday
              ?.OFF_PEAK || 0;

          totals.month_to_date.TOTAL +=
            child.month_to_date
              ?.TOTAL || 0;

          totals.month_to_date.PEAK +=
            child.month_to_date
              ?.PEAK || 0;

          totals.month_to_date.OFF_PEAK +=
            child.month_to_date
              ?.OFF_PEAK || 0;

          totals.year_to_date.TOTAL +=
            child.year_to_date
              ?.TOTAL || 0;

          totals.year_to_date.PEAK +=
            child.year_to_date
              ?.PEAK || 0;

          totals.year_to_date.OFF_PEAK +=
            child.year_to_date
              ?.OFF_PEAK || 0;
        }
      );

      node.yesterday =
        totals.yesterday;

      node.month_to_date =
        totals.month_to_date;

      node.year_to_date =
        totals.year_to_date;
    }

    return node;
  });
};
const DAM_EXPENSES = [
  "Gas",
  "Duct Fire",
  "Field Load",
  "GHG",
  "GMC",
  "LTSA",
  "VOM",
];
const isTodayOrTomorrow = (
  dateStr: string
) => {

  const selected =
    new Date(dateStr);

  selected.setHours(
    0,0,0,0
  );

  const today =
    new Date();

  today.setHours(
    0,0,0,0
  );

  const tomorrow =
    new Date(today);

  tomorrow.setDate(
    tomorrow.getDate() + 1
  );

  return (
    selected.getTime() ===
      today.getTime() ||
    selected.getTime() ===
      tomorrow.getTime()
  );
};
export const useAssetRevenue = (
  assetId: number | null,
  selectedDateRange: string,
  selectedRevenue: string
) => {

  const [data, setData] =
    useState<RevenueData | null>(
      null
    );

  const [loading, setLoading] =
    useState(false);
//     console.log(
//   "HOOK INPUTS",
//   {
//     assetId,
//     selectedDateRange,
//     selectedRevenue
//   }
// );

  useEffect(() => {

    // if (!assetId) {
    //   setData(null);
    //   return;
    // }
// if (
//   !assetId ||
//   !selectedDateRange ||
//   selectedDateRange === "yesterday"
// ) {
//   return;
// }
if (!assetId) {
  setData(null);
  return;
}

if (
  !selectedDateRange ||
  selectedDateRange === "yesterday"
) {
  return;
}
    let cancelled = false;

    setLoading(true);

    const load = async () => {
  //      console.log(
  //   "ACTUAL API REQUEST",
  //   assetId,
  //   selectedDateRange,
  //   new Date().toISOString()
  // );

      try {

        const token =
          await getAuthToken();

        const baseUrl =
          process.env
            .REACT_APP_ANALYTICS_API as string;

        const headers = {
          Authorization:
            `Bearer ${token}`,

          "Ocp-Apim-Subscription-Key":
            process.env
              .REACT_APP_SUBSCRIPTION_KEY_TIMESERIES ?? "",

          Product:
            process.env
              .REACT_APP_PRODUCT_APIM,
        };

        const payload = {
          filters: {
            asset_id: {
              operator: "EQ",
              value: Number(assetId),
            },
          },

          metric_group:
            "ASSET_SETTLEMENT_DETAIL",

          operation:
            "GET_ASSET_SETTLEMENT_DETAIL",

          date_range: {
            start_date:
              selectedDateRange,
          },

          time_granularity:
            "day",

          // group_by:
          //   "settlement_charge_name",
          group_by:
            selectedRevenue || "settlement_charge_name",

          user:
            localStorage.getItem(
              "userEmail"
            ),
        };

        const response =
          await axios.post(
            `${baseUrl}/revenue/peak-breakdown`,
            payload,
            { headers }
          );

        if (cancelled) return;

        const apiData =
          response.data;
          const showDamExpense =
  isTodayOrTomorrow(
    selectedDateRange
  );

        // console.log(
        //   "PEAK BREAKDOWN RESPONSE",
        //   apiData
        // );

        
  const sortMap =
  buildSortMap(
    apiData.sort_order || []
  );
// console.log(
//   "DAM",
//   sortMap.get("DAM")
// );

// console.log(
//   "RTUC",
//   sortMap.get("RTUC")
// );

// console.log(
//   "RTM",
//   sortMap.get("RTM")
// );
const sortedApiData = {
  ...apiData,

  day: sortNodes(
    apiData.day || [],
    sortMap
  ),

  month_to_date:
    sortNodes(
      apiData.month_to_date || [],
      sortMap
    ),

  year_to_date:
    sortNodes(
      apiData.year_to_date || [],
      sortMap
    ),
};
// console.log(
//   "TOP LEVEL",
//   sortedApiData.day.map(
//     (x: any) => x.metric
//   )
// );
// console.log(
//   "DAY",
//   sortedApiData.day
// );

// console.log(
//   "MTD",
//   sortedApiData.month_to_date
// );

// console.log(
//   "YTD",
//   sortedApiData.year_to_date
// );
// const estimatedTree =
//   calculateParentTotals(
//     mergePeriods(
//       sortedApiData
//     )
//   );
let estimatedTree =
  calculateParentTotals(
    mergePeriods(sortedApiData)
  );
estimatedTree = estimatedTree.map(
  (node: any) => {
    if (
      node.label?.trim() === "DAM" &&
      node.children
    ) {
      return {
        ...node,
        children: node.children.filter(
          (child: any) =>
            child.label
              ?.trim()
              .toLowerCase() !==
            "expense"
        ),
      };
    }

    return node;
  }
);

        setData({
          estimatedTree,
        });

      } catch (err) {

        console.error(
          "Revenue API Error",
          err
        );

        if (!cancelled) {
          setData({
            estimatedTree: [],
          });
        }

      } finally {

        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };

  }, [
    assetId,
    selectedDateRange,
    selectedRevenue,
  ]);

  return {
    data,
    loading,
  };
};